import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { IoMdNotifications } from 'react-icons/io'
import { FiClock, FiCheckCircle, FiAlertTriangle, FiInfo, FiXCircle, FiZap, FiRepeat } from 'react-icons/fi'
import { fetchDashboardUser } from './userSession'
import { buildNotifications, loadReadIds, saveReadIds } from './notifications'

const TONE_ICON = {
  info: <FiInfo />, success: <FiCheckCircle />, warning: <FiAlertTriangle />,
  danger: <FiXCircle />, tip: <FiZap />, neutral: <FiRepeat />,
}

const SECTIONS = [
  ['activity', 'Recent activity'],
  ['status', 'Your account'],
  ['tip', 'Tips'],
]

// Sticky header shared by every user dashboard page.
// `leading` replaces the title, e.g. with a back button on multi-step pages.
const DashboardTopbar = ({ route, title, leading }) => {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [open, setOpen] = useState(false)
  const [readIds, setReadIds] = useState(new Set())
  const panelRef = useRef(null)

  useEffect(() => {
    let active = true
    fetchDashboardUser(route).then(data => {
      if (!active) return
      setUser(data)
      setReadIds(loadReadIds(data))
    })
    return () => { active = false }
  }, [route])

  // close on outside click / Escape
  useEffect(() => {
    if (!open) return
    const onClick = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const notifications = useMemo(() => buildNotifications(user), [user])
  const unreadCount = notifications.filter(n => !readIds.has(n.id)).length

  const markRead = (ids) => {
    const next = new Set([...readIds, ...ids])
    setReadIds(next)
    saveReadIds(user, next)
  }

  const openItem = (item) => {
    markRead([item.id])
    setOpen(false)
    navigate(item.link)
  }

  const initials = `${user?.firstname?.[0] || ''}${user?.lastname?.[0] || ''}`.toUpperCase() || '—'

  return (
    <header className="ud-topbar">
      <div className="ud-topbar-left">
        {leading || <p className="ud-greeting">{title}</p>}
      </div>
      <div className="ud-topbar-right">
        <div className="ud-notif-wrap" ref={panelRef}>
          <button
            className={`ud-notif-btn${open ? ' open' : ''}`}
            onClick={() => setOpen(o => !o)}
            aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
            aria-expanded={open}
          >
            <IoMdNotifications />
            {unreadCount > 0 && <span className="ud-notif-count">{unreadCount > 9 ? '9+' : unreadCount}</span>}
          </button>

          {open && (
            <div className="ud-notif-panel" role="dialog" aria-label="Notifications">
              <div className="ud-notif-head">
                <p>Notifications</p>
                {unreadCount > 0 && (
                  <button className="ud-notif-markall" onClick={() => markRead(notifications.map(n => n.id))}>
                    Mark all as read
                  </button>
                )}
              </div>

              <div className="ud-notif-body">
                {notifications.length === 0 && <p className="ud-notif-empty">You're all caught up.</p>}
                {SECTIONS.map(([kind, label]) => {
                  const items = notifications.filter(n => n.kind === kind)
                  if (!items.length) return null
                  return (
                    <div key={kind} className="ud-notif-section">
                      <p className="ud-notif-section-label">{label}</p>
                      {items.map(item => {
                        const unread = !readIds.has(item.id)
                        return (
                          <button key={item.id} className={`ud-notif-item${unread ? ' unread' : ''}`} onClick={() => openItem(item)}>
                            <span className={`ud-notif-icon ${item.tone}`}>{TONE_ICON[item.tone] || <FiInfo />}</span>
                            <span className="ud-notif-text">
                              <span className="ud-notif-title">{item.title}</span>
                              <span className="ud-notif-desc">{item.text}</span>
                              {item.time && <span className="ud-notif-time"><FiClock size={10} /> {item.time}</span>}
                            </span>
                            {unread && <span className="ud-notif-dot-unread" aria-hidden="true" />}
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>

              <Link to="/login-history" className="ud-notif-foot" onClick={() => setOpen(false)}>
                View sign-in history
              </Link>
            </div>
          )}
        </div>
        <p className="ud-topbar-name">Hi, <span>{user?.firstname || '—'}</span></p>
        <Link to="/settings" className="ud-user-avatar ud-avatar-initials" aria-label="Account settings">{initials}</Link>
      </div>
    </header>
  )
}

export default DashboardTopbar
